import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}

/* Magic Candles: visible, muted mobile videos — 2026-09-30. */
(() => {
  const mobile = matchMedia('(max-width: 749px)');
  const items = new Map();
  const attributes = ['autoplay', 'muted', 'playsinline', 'loop'];
  const configure = (state) => {
    const video = state.video;
    if (!video || state.configured) return;
    state.original = attributes.map((name) => video.getAttribute(name));
    state.originalMuted = video.muted;
    state.wasPlaying = !video.paused;
    video.muted = true;
    video.defaultMuted = true;
    video.autoplay = true;
    video.playsInline = true;
    video.loop = true;
    state.configured = true;
  };
  const syncHint = (state) => {
    if (state.host.localName !== 'deferred-media') return;
    state.host.isPlaying = !state.video.paused;
    state.host.updatePlayPauseHint(state.host.isPlaying);
  };
  const attach = (state, video) => {
    state.video = video;
    state.host.addEventListener('click', (event) => {
      if (event.target.closest('[ref="toggleMediaButton"]')) state.userPaused = !video.paused;
      if (event.target.closest('[ref="deferredMediaPlayButton"]')) state.userPaused = false;
    }, true);
    video.addEventListener('play', () => {
      state.userPaused = false;
      syncHint(state);
    });
    video.addEventListener('pause', () => {
      syncHint(state);
    });
  };
  const stop = (state) => {
    if (!state.configured || !state.video) return;
    state.autoPaused = true;
    state.video.pause();
  };
  const start = (state) => {
    if (!mobile.matches || !state.visible || document.hidden || state.userPaused || !state.host.isConnected) return;
    if (!state.video) {
      state.host.loadContent(false);
      const video = state.host.querySelector('video');
      if (!video) return;
      attach(state, video);
    }
    configure(state);
    state.autoPaused = false;
    const result = state.video.play();
    if (result) result.catch(() => syncHint(state));
  };
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const state = items.get(entry.target);
      if (!state) continue;
      state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
      if (state.visible) start(state);
      else if (mobile.matches) stop(state);
    }
  }, { threshold: [0, 0.15] });
  const scan = () => {
    for (const [host, state] of items) {
      if (!host.isConnected) { stop(state); observer.unobserve(host); items.delete(host); }
    }
    document.querySelectorAll('#MainContent deferred-media').forEach((host) => {
      if (items.has(host) || (host.localName === 'video' && host.closest('deferred-media'))) return;
      if (host.localName === 'deferred-media' && host.querySelector('template')?.content.firstElementChild?.tagName !== 'VIDEO') return;
      const state = { host, video: null, visible: false, userPaused: false, configured: false, autoPaused: false };
      const video = host.localName === 'video' ? host : host.querySelector('video');
      if (video) attach(state, video);
      items.set(host, state);
      observer.observe(host);
    });
  };
  mobile.addEventListener('change', () => {
    for (const state of items.values()) {
      if (mobile.matches) { if (state.visible) start(state); }
      else if (state.configured) {
        stop(state);
        attributes.forEach((name, index) => {
          const value = state.original[index];
          if (value === null) state.video.removeAttribute(name);
          else state.video.setAttribute(name, value);
        });
        state.video.muted = state.originalMuted;
        state.configured = false;
        if (state.wasPlaying && state.visible) state.video.play().catch(() => syncHint(state));
      }
    }
  });
  document.addEventListener('visibilitychange', () => {
    if (!mobile.matches) return;
    for (const state of items.values()) {
      if (document.hidden) stop(state);
      else start(state);
    }
  });
  window.addEventListener('pageshow', () => { for (const state of items.values()) start(state); });
  let pending = false;
  const schedule = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => { pending = false; scan(); });
  };
  scan();
  new MutationObserver(schedule).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
})();
